import React, { useEffect, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print';
import FinanceLayout from '../../../../../../layout/components/FinanceLayout'
import { useParams } from 'react-router-dom'
import inventoryTransferServices from '../inventoryTransferServices';
import { useAppData } from '../../../../../../context/AppDataContext';
import moment from 'moment';
import SelectProduct from '../../product/components/SelectProduct';
import InventoryTransferItemForm from '../components/InventoryTransferItemForm';
import inventoryTransferItemServices from '../inventoryTransferItemServices';
import { useAlert } from '../../../../../../context/AlertContext';
import { IconButton } from '@mui/material';
import ConfirmDelete from '../../../../../../components/ConfirmDelete';
import { Print } from '@mui/icons-material';
import companyServices from '../../company/companyServices';
import LatterHead from '../../../crm/proposal/section/LatterHead';

function InventoryTransferDetailsPage() {
    const { id } = useParams();
    const printComponentRef = useRef();
    const [transferDetails, setTransferDetails] = useState(null);
    const [transferItemList, setTransferItemList] = useState(null);
    const { productList, getProductList, companyLocationDD, getDDDataById, getAppDataById } = useAppData();
    const { inventoryStockList, getInventoryStockList } = useAppData();
    const { AppAlert } = useAlert();
    const [companyDetails, setCompanyDetails] = useState(null);

    const [savingStatus, setSavingStatus] = useState(false);

    const getTransferDetails = () => {
        inventoryTransferServices.getById(id).then(res => {
            setTransferDetails(res.data);
        }).catch(error => {
            console.error(error);
        });
    };

    const getCompanyDetails = (companyId) => {
        companyServices.getById(companyId).then(res => {
            setCompanyDetails(res.data);
        }).catch(error => console.log(error));
    }

    const getTransferItemList = () => {
        inventoryTransferItemServices.getByTransferId(id).then(res => {
            setTransferItemList(res.data);
        }).catch(error => {
            console.error(error);
        });
    };

    useEffect(() => {
        getTransferDetails();
        getInventoryStockList();
        getProductList();
        getTransferItemList();
    }, [id]);

    useEffect(() => {
        if (transferDetails && !companyDetails) {
            getCompanyDetails(transferDetails.companyId);
        }
    }, [transferDetails]);

    const finalize = () => {
        setSavingStatus(true);
        inventoryTransferServices.finalize(id).then(res => {
            AppAlert(res.msg);
            getTransferDetails();
        }).catch(error => {
            AppAlert(error.msg, "error");
        }).finally(() => {
            setSavingStatus(false);
        });
    };

    // 1. Filter logic: Filter by location AND exclude items already added to the transfer list
    const getFilteredStockList = () => {
        if (!inventoryStockList || !transferDetails) return [];
        
        // Filter by location first
        let stockFiltered = inventoryStockList.filter(stock => stock.locationId === transferDetails.fromLocationId);
        
        // Exclude products already in the transfer list
        if (transferItemList) {
            const addedProductIds = transferItemList.map(item => item.productId);
            stockFiltered = stockFiltered.filter(stock => !addedProductIds.includes(stock.productId));
        }
        
        return stockFiltered;
    };

    const remove = (id) =>{
        inventoryTransferItemServices.remove(id).then(res=>{
            AppAlert("Item Remove");
            getTransferItemList();
        }).catch(error=>{

        })
    }

    const handlePrint = useReactToPrint({
        content: () => printComponentRef.current,
        pageStyle: `@page { size: auto; margin: 10mm; } @media print { body { -webkit-print-color-adjust: exact; } }`
    });

    return (
        <FinanceLayout>
            <style>
            {
                `
                table {page-break-after:unset}
                `
            }
            </style>
            <div className="container-fluid py-4">
                {/* STATUS 1: DRAFT / PENDING TRANSFER */}
                {transferDetails && transferDetails.status === 1 && (
                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
                            <div>
                                <h5 className="mb-1 text-primary FW-bold">Transfer Voucher: #{transferDetails.id}</h5>
                                <p className="text-muted small mb-0">Date: {moment(transferDetails.transferDate).format("LL")}</p>
                            </div>
                            <button 
                                className='btn btn-success px-4' 
                                onClick={finalize} 
                                disabled={savingStatus || !transferItemList || transferItemList.length === 0}
                            >
                                {savingStatus ? 'Finalizing...' : 'Finalize Stock Transfer'}
                            </button>
                        </div>
                        
                        <div className="card-body">
                            <div className="alert alert-light border mb-4 d-flex align-items-center">
                                <div className="fw-semibold text-secondary me-2">Route:</div>
                                <div>
                                    <span className="badge bg-secondary p-2">{getDDDataById(companyLocationDD, transferDetails.fromLocationId)?.label}</span>
                                    <span className="mx-2 text-muted">➔</span>
                                    <span className="badge bg-primary p-2">{getDDDataById(companyLocationDD, transferDetails.toLocationId)?.label}</span>
                                </div>
                            </div>

                            <div className='table-responsive'>
                                <table className='table table-hover align-middle'>
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '80px' }}>#</th>
                                            <th>Item Name</th>
                                            <th className="text-end" style={{ width: '150px' }}>Quantity</th>
                                            <th className="text-end" style={{ width: '20px' }}>Rate</th>
                                            <th className="text-end" style={{ width: '200px' }}>Total Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transferItemList?.map((transferItem, i) => {
                                            // Explicitly parse values to prevent string type crashes
                                            const cost = Number(transferItem.cost) || 0;
                                            const qty = Number(transferItem.qty) || 0;
                                            const totalAmount = cost * qty;

                                            return (
                                                <tr key={transferItem.id} className="hover-container">
                                                    <td>{i + 1}</td>
                                                    <td className="fw-medium">
                                                        {getAppDataById(productList, transferItem.productId)?.title}
                                                        {getAppDataById(productList, transferItem.productId)?.hsn}
                                                        
                                                        {transferDetails.status === 1 && (
                                                            <ConfirmDelete id={transferItem.id} confirm={remove}>
                                                                Remove {getAppDataById(productList, transferItem.productId)?.title} from transfer list?
                                                            </ConfirmDelete>
                                                        )}
                                                    </td>
                                                    <td className="text-end">{qty}</td>
                                                    <td className="text-end">{cost.toFixed(2)}</td>
                                                    <td className="text-end fw-bold">{totalAmount.toFixed(2)}</td>
                                                </tr>
                                            );
                                        })}
                                        
                                        {/* Row for adding new items */}
                                        <tr>
                                            <td colSpan={5} className="bg-light p-3">
                                                {inventoryStockList && productList && transferItemList && (
                                                    <InventoryTransferItemForm
                                                        transferDetails={transferDetails} 
                                                        callBack={getTransferItemList} 
                                                        inventoryStockList={getFilteredStockList()} 
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* STATUS 2: COMPLETED / FINALIZED TRANSFER */}
                {transferDetails && transferDetails.status === 2 && (
                    <div className="card shadow-sm border-0 mb-4" >
                        <div className="card-header bg-success text-white py-3 d-flex justify-content-between align-items-center">
                            <div>
                                <h5 className="mb-1 text-white fw-bold">Transfer Voucher: #{transferDetails.id}</h5>
                                <p className="small mb-0 opacity-75">Issued On: {moment(transferDetails.transferDate).format("LL")}</p>
                            </div>
                            <span className="badge bg-white text-success px-3 py-2 fw-bold text-uppercase">Finalized / Posted</span>

                            <button className='btn btn-primary' onClick={handlePrint} ><Print/> Print</button>
                        </div>

                        <div className="card-body" ref={printComponentRef}>
                            {/* Route summary details */}
                            <LatterHead companyDetails={companyDetails} />
                            <div className="row g-3 mb-4 text-center">
                                <div className="col-5">
                                    <div className="p-3 bg-light border rounded">
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wider">Source Location</small>
                                        <span className="fs-5 fw-bold text-dark">{getDDDataById(companyLocationDD, transferDetails.fromLocationId)?.label}</span>
                                    </div>
                                </div>
                                <div className="col-2 d-flex align-items-center justify-content-center">
                                    <span className="fs-3 text-success">➔</span>
                                </div>
                                <div className="col-5">
                                    <div className="p-3 bg-light border rounded">
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wider">Destination Location</small>
                                        <span className="fs-5 fw-bold text-dark">{getDDDataById(companyLocationDD, transferDetails.toLocationId)?.label}</span>
                                    </div>
                                </div>
                            </div>

                            { transferDetails.narration !== "" &&
                                <div className='p-1 border rounded mb-2'>
                                    {transferDetails.narration}
                                </div>
                            }

                            {/* Read-only historical breakdown list */}
                            <h6 className="fw-bold mb-3 text-secondary">Transferred Items Summary</h6>
                            <div className='table-responsive'>
                                <table className='table table-striped align-middle border text-secondary rounded'>
                                    <thead className="table-dark">
                                        <tr>
                                            <th style={{ width: '80px' }}>#</th>
                                            <th>Item Name</th>
                                            <th className="text-end" style={{ width: '150px' }}>Quantity Transferred</th>
                                            <th className="text-end" style={{ width: '150px' }}>Rate</th>
                                            <th className="text-end" style={{ width: '20px' }}>Total Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transferItemList?.map((transferItem, i) => {
                                            // Explicitly parse values to prevent string type crashes
                                            const cost = Number(transferItem.cost) || 0;
                                            const qty = Number(transferItem.qty) || 0;
                                            const totalAmount = cost * qty;

                                            return (
                                                <tr key={transferItem.id}>
                                                    <td>{i + 1}</td>
                                                    <td className="fw-medium">
                                                        {getAppDataById(productList, transferItem.productId)?.title}
                                                        <span className='btn btn-secondary btn-sm ms-2'>{getAppDataById(productList, transferItem.productId)?.hsn}</span>
                                                    </td>
                                                    <td className="text-end">{qty}</td>
                                                    <td className="text-end">{cost.toFixed(2)}</td>
                                                    <td className="text-end fw-bold">{totalAmount.toFixed(2)}</td>
                                                </tr>
                                            );
                                        })}
                                        {(!transferItemList || transferItemList.length === 0) && (
                                            <tr>
                                                <td colSpan={5} className="text-center text-muted py-3">No items found in this transfer voucher.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className='row'>
                                <div className='col-6'></div>
                                <div className='col-6'>
                                    <div className='card' style={{fontSize:12}}>
                                        <div className='card-body p-1'>
                                        <div className='float-end'>
                                            <div className='fw-bold'>for {companyDetails?.companyName}</div>
                                            <img src={companyDetails?.authSign} style={{ maxWidth:".75in",maxHeight:".75in",}} />
                                            <div>Authorised Signatory</div>
                                        </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </FinanceLayout>
    );
}

export default InventoryTransferDetailsPage;